import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import supabase from '../config/supabaseClient';
import {
  fetchSubscription,
  isSubscriptionActive,
  isTrialing,
  trialDaysRemaining,
} from '../services/billingService';

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const channelIdRef = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
  );

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const sub = await fetchSubscription(user.id);
      setSubscription(sub);
    } catch (err) {
      console.error('useSubscription fetch error', err);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const channel = supabase
      .channel(`profile-sub-${user.id}-${channelIdRef.current}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `user_id=eq.${user.id}` },
        (payload) => {
          const row = payload.new || {};
          setSubscription((prev) => ({
            ...(prev || {}),
            subscription_status: row.subscription_status,
            subscription_id: row.subscription_id,
            trial_end: row.trial_end,
            current_period_start: row.current_period_start,
            current_period_end: row.current_period_end,
            price_tier: row.price_tier,
            canceled_at: row.canceled_at,
            stripe_customer_id: row.stripe_customer_id,
          }));
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return {
    subscription,
    loading,
    isActive: isSubscriptionActive(subscription),
    isTrialing: isTrialing(subscription),
    trialDaysRemaining: trialDaysRemaining(subscription),
    refresh,
  };
}
